import React, {useMemo} from "react";
import {
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

import {
  calculatePdfFontSize,
  calculatePdfHeight,
  calculatePdfWidth,
} from "../../../Utils";
import {FormPrintOptionsType} from "../../../Types";
import {PdfLabelCalculationType} from "../Types/Pdf";

export const PdfCalculation = ({printOptions, label, colorRed}: { printOptions: FormPrintOptionsType, label: PdfLabelCalculationType, colorRed: string }) => {
  const styles = StyleSheet.create({
    discountPriceRaw: {
      display: "flex",
      flexDirection: "row",
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      color: colorRed,
      textAlign: "center",
      fontFamily: "Sunborn",
    },
    discountPriceWhole: {
    },
    discountPriceDecimalRaw: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: calculatePdfWidth(20, printOptions.size),
    },
    discountPriceDecimalEuro: {
    },
    discountPriceDecimal: {
    },
    discountDetails: {
      minHeight: calculatePdfHeight(60, printOptions.size),
    },
    discountDetailImportant: {
      color: colorRed,
      textAlign: "center",
      fontFamily: "Sunborn",
      fontSize: calculatePdfFontSize(25, printOptions.size),
      lineHeight: calculatePdfHeight(25, printOptions.size) + "px",
      textTransform: "uppercase",
    },
    discountDetail: {
      color: "black",
      textAlign: "center",
      fontFamily: "Sunborn",
      fontSize: calculatePdfFontSize(20, printOptions.size),
      marginTop: calculatePdfWidth(7, printOptions.size),
      textTransform: "uppercase",
    },
    originalPrice: {
      position: "relative",
    },
    originalPriceRaw: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      justifyContent: 'flex-end',
      color: "black",
      fontFamily: "Sunborn",
      marginRight: calculatePdfWidth(40, printOptions.size),
      marginTop: calculatePdfWidth(20, printOptions.size),
    },
    originalPriceStrike: {
      position: "absolute",
      right: calculatePdfWidth(15, printOptions.size),
      paddingLeft: calculatePdfWidth(10, printOptions.size),
      paddingRight: calculatePdfWidth(10, printOptions.size),
      borderTop: calculatePdfFontSize(5, printOptions.size) + "px solid black",
      transform: "rotate(-30deg)",
      color: "transparent",
    },
    originalPriceWhole: {
      paddingRight: calculatePdfWidth(10, printOptions.size),
    },
    originalPriceDecimalRaw: {
      display: "flex",
      flexDirection: "column",
    },
    originalPriceDecimalEuro: {
      fontSize: calculatePdfFontSize(17, printOptions.size),
      lineHeight: calculatePdfHeight(17, printOptions.size) + "px",
    },
    originalPriceDecimal: {
      fontSize: calculatePdfFontSize(17, printOptions.size),
      lineHeight: calculatePdfHeight(17, printOptions.size) + "px",
    },
  });

  const themeDiscountPriceRaw = useMemo(() => ({
    portrait: {
      marginTop: calculatePdfHeight(-20, printOptions.size),
      marginBottom: calculatePdfHeight(-120, printOptions.size),
    },
    landscape: {
      marginTop: calculatePdfHeight(-60, printOptions.size),
    }
  }), [printOptions.size]);

  const themeDiscountPriceWhole = useMemo(() => ({
    portrait: {
      fontSize: calculatePdfFontSize(330, printOptions.size),
    },
    landscape: {
      fontSize: calculatePdfFontSize(280, printOptions.size),
    }
  }), [printOptions.size]);

  const themeDiscountPriceDecimalEuro = useMemo(() => ({
    portrait: {
      fontSize: calculatePdfFontSize(105, printOptions.size),
    },
    landscape: {
      fontSize: calculatePdfFontSize(90, printOptions.size),
    }
  }), [printOptions.size]);

  const themeDiscountPriceDecimal = useMemo(() => ({
    portrait: {
      fontSize: calculatePdfFontSize(105, printOptions.size),
    },
    landscape: {
      fontSize: calculatePdfFontSize(70, printOptions.size),
    }
  }), [printOptions.size]);

  const themeOriginalPrice = useMemo(() => ({
    portrait: {
    },
    landscape: {
      marginTop: calculatePdfHeight(-172, printOptions.size),
    }
  }), [printOptions.size]);

  const themeOriginalPriceStrike = useMemo(() => ({
    portrait: {
      fontSize: calculatePdfFontSize(60, printOptions.size),
      top: calculatePdfHeight(50, printOptions.size),
    },
    landscape: {
      fontSize: calculatePdfFontSize(40, printOptions.size),
      top: calculatePdfHeight(35, printOptions.size),
    }
  }), [printOptions.size]);

  const themeOriginalPriceWhole = useMemo(() => ({
    portrait: {
      fontSize: calculatePdfFontSize(60, printOptions.size),
      lineHeight: calculatePdfHeight(60, printOptions.size) + "px",
    },
    landscape: {
      fontSize: calculatePdfFontSize(40, printOptions.size),
      lineHeight: calculatePdfHeight(40, printOptions.size) + "px",
    }
  }), [printOptions.size]);

  const themeOriginalPriceDecimalEuro = useMemo(() => ({
    portrait: {
      paddingTop: calculatePdfHeight(25, printOptions.size),
    },
    landscape: {
      paddingTop: calculatePdfHeight(6, printOptions.size),
    }
  }), [printOptions.size]);

  // @ts-ignore
  return (
    <>
      {/* @ts-ignore */}
      <View style={{...styles.discountPriceRaw, ...themeDiscountPriceRaw[printOptions.orientation]}}>
        {/* @ts-ignore */}
        <Text style={{...styles.discountPriceWhole, ...themeDiscountPriceWhole[printOptions.orientation]}}>
          {!label.discountPrice.whole ? ' ' : label.discountPrice.whole}
        </Text>

        {label.discountPrice.decimals && (
          <View style={styles.discountPriceDecimalRaw}>
            {/* @ts-ignore */}
            <Text style={{...styles.discountPriceDecimalEuro, ...themeDiscountPriceDecimalEuro[printOptions.orientation]}}>
              €
            </Text>
            {/* @ts-ignore */}
            <Text style={{...styles.discountPriceDecimal, ...themeDiscountPriceDecimal[printOptions.orientation]}}>
              ,{label.discountPrice.decimals}
            </Text>
          </View>
        )}
      </View>

      {'portrait' === printOptions.orientation && (
        <View style={styles.discountDetails}>
          {label.discountUnitPrice !== '' && (
            <Text style={styles.discountDetailImportant}>
              {label.discountUnitPrice}
            </Text>
          )}

          {(label.originalUnitPrice !== '' || label.discountDetail !== '') && (
            <Text style={styles.discountDetail}>
              {label.originalUnitPrice !== '' && label.originalUnitPrice}
              {label.discountDetail !== '' && label.discountDetail}
            </Text>
          )}
        </View>
      )}

      {label.originalPrice.whole && label.originalPrice.decimals && (
        <View style={{...styles.originalPrice, ...themeOriginalPrice[printOptions.orientation]}}>
          {/* @ts-ignore */}
          <View style={{...styles.originalPriceStrike, ...themeOriginalPriceStrike[printOptions.orientation]}}>
            <Text>{label.originalPrice.whole}€</Text>
          </View>

          <View style={styles.originalPriceRaw}>
            {/* @ts-ignore */}
            <Text style={{...styles.originalPriceWhole, ...themeOriginalPriceWhole[printOptions.orientation]}}>
              {label.originalPrice.whole}
            </Text>

            <View style={styles.originalPriceDecimalRaw}>
              {/* @ts-ignore */}
              <Text style={{...styles.originalPriceDecimalEuro, ...themeOriginalPriceDecimalEuro[printOptions.orientation]}}>
                €
              </Text>
              <Text style={{...styles.originalPriceDecimal}}>
                ,{label.originalPrice.decimals}
              </Text>
            </View>
          </View>
        </View>
      )}
    </>
  );
};

export default PdfCalculation;
